import React, {useState, useEffect} from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import cn from 'classnames';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {useMutation} from '@tanstack/react-query';
import {Panel, Form, Icon, InputField, Button, BUTTON_THEMES, CalloutBox, CalloutBoxThemes, htToast} from 'ht-styleguide';
import Maybe from '@components/Maybe';

/* Apis */
import {locations} from '@api/apis';

/* Store */
import useServiceSkuStore from '@scenes/ServiceSkuPage/store/store.servicesku';

/* Cache/Mutations */
import useUserCache from '@store/queries/user/useUser.cache';
import useCartMutationAddCoupon from 'src/store/queries/cart/useCartMutation.addcoupontocart';

/* Actions & Hooks */
import {SegmentTrackingAction} from '@integrations/segment/geo';
import {getSetHookFormError, useExternalEmailCheck} from '@components/UI/EmailFieldWithValidation/EmailFieldWithValidation.hooks';

/* Constants */
import {zipOnlyTestTreatment, zipCodeCta} from '@components/Sku/ZipCodeEntry/zipcodeentry.constants';
import {privacyPath} from '@constants/paths';
import {YupSchemaResolver} from '@constants/validations';
import {HT_INFO_PHONE} from '@constants/hellotech';

/* Utils */
import {setZipDataCookie, getZipDataCookie} from '@utils/cookies/zipCodeTestCookie';
import {Logger} from '@utils/logger';

/* Integrations */
import {SPLITIONAME_CUSTOMER_ZIP, SplitTreatmentValuesEnum} from '@integrations/split/split.constants';
import useSplit from '@integrations/split/useSplit';
import {logWarning} from '@integrations/bugsnag';
import Chat from '@integrations/gladly/Chat/Gladly.methods';

/* Components */
import ChatLink from '@integrations/gladly/Chat/ChatLink';

/* Types */
import {Sku} from 'types/sku.types';

/* Styles */
import styles from './zipcodeentry.module.scss';

/* Dynamic Imports */
const SkuZipCodeEntrySuccessDynamic = dynamic(() => import('@components/Sku/ZipCodeEntry/ZipCodeSkuInfo.Success'), {ssr: false});
const SkuZipCoverageModalDynamic = dynamic(() => import('./ZipCodeEntry.CoverageModal'), {ssr: false});
const EmailFieldWithValidation = dynamic(() => import('@components/UI/EmailFieldWithValidation').then(module => module.EmailFieldWithValidation), {
  ssr: false,
});

/* Inline Types */
export type ZipSplitTest = boolean | string;
type YupFormProps = {zipCode: string; email: string};
type ErrorCalloutBoxProps = {
  zip: string;
  className: string;
  visible: boolean;
};

type ServiceZipCodeEntryProps = {
  className?: string;
  onUpsellMore: BaseAnyFunction;
  onUpsellToggle: BaseAnyFunction;
  sku: Sku & {showZipCodeTest: boolean};
};

export enum ZipValidationState {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

type TosDisplayProps = {
  showZipOnly: boolean;
  privacyPath: () => string;
};

const ErrorCalloutBox = ({zip = '', visible, className}: ErrorCalloutBoxProps) => {
  if (!visible) return null;
  return (
    <div className={cn(styles.errorCallout, className)}>
      <CalloutBox
        text={`We apologize for the inconvenience. We do not currently offer service in your zip code ${zip}.`}
        theme={CalloutBoxThemes.CRITICAL}
      />
    </div>
  );
};

const ZipCodeEntry = ({className = '', sku, onUpsellMore, onUpsellToggle}: ServiceZipCodeEntryProps) => {
  /* Local State */
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /* Store */
  const {zipValidationState, setZipValidationState} = useServiceSkuStore(state => state.zipCodeGate);

  /* Hooks */
  const {treatment: splitTreatment} = useSplit(SPLITIONAME_CUSTOMER_ZIP);
  const user = useUserCache();

  /* Cookie Data */
  const zipDataCookie = getZipDataCookie() || {isBookingAllowed: true};

  /* Page constants */
  const isZipSuccessState = zipValidationState === ZipValidationState.SUCCESS;
  const isZipErrorState = zipValidationState === ZipValidationState.ERROR;
  const showZipOnly = [zipOnlyTestTreatment, SplitTreatmentValuesEnum.CONTROL, SplitTreatmentValuesEnum.SEED_CONTROL].includes(splitTreatment);
  const zipTreatmentIsControl = splitTreatment.includes(SplitTreatmentValuesEnum.CONTROL);
  const errorCalloutMobileTabStyles = cn('showMobileTablet width-full', {
    'marginBottom-medium': showZipOnly,
    'marginTop-small1': !showZipOnly,
  });

  /* Hooks */
  const {
    reset,
    register,
    handleSubmit,
    setValue,
    setError,
    formState: {errors},
    watch,
    control,
  } = useForm<YupFormProps>({
    ...YupSchemaResolver(['zipCode', !showZipOnly && 'email'], undefined, {
      zipCode: '',
      email: '',
    }),
  });

  /* Emailable Verify Behavior */
  const {validateEmailWithConfig, emailValidationState, useSuggestion, handleOnChange} = useExternalEmailCheck({
    useValidationService: !showZipOnly,
    setValue,
    setError: getSetHookFormError<keyof YupFormProps>(setError),
    fieldName: 'email',
    onSuccess: () => {
      const zipCode = watch('zipCode');
      const email = watch('email');
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      onSubmitForm({zipCode, email});
    },
  });

  const getIsBookingAllowed = (treatment: SplitTreatmentValuesEnum): boolean =>
    [SplitTreatmentValuesEnum.ON, SplitTreatmentValuesEnum.CONTROL].includes(treatment);

  /* Mutation Callbacks */
  const onValidZip = (response: any, zip: string) => {
    const {treatment} = response.data || {};

    if (response.err) throw new Error(response.err);
    if (!treatment) throw new Error(String(response));

    const isBookingAllowed = getIsBookingAllowed(treatment);
    setZipDataCookie({...zipDataCookie, zipCode: zip, isBookingAllowed});
    setZipValidationState(isBookingAllowed ? ZipValidationState.SUCCESS : ZipValidationState.ERROR);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (!isBookingAllowed) openModal();
  };

  const onInvalidZip = (error: Error, zip: string) => {
    setIsLoading(false);
    setZipDataCookie({...zipDataCookie, zipCode: zip, isBookingAllowed: true});
    setZipValidationState(ZipValidationState.SUCCESS);
    // User put in bad zip, no need to log: Logger('ZipCodeEntry - verifyIsBookingAllowed')(error as Error);
  };

  const onAddCouponSuccess = () => {
    htToast('10% off will be applied in your cart!');
  };

  const onAddCouponError = (error: unknown) => {
    setIsLoading(false);
    // If we get an error, cannot disrupt user behavior. Log it and move on.
    Logger('ZipCodeEntry - addCouponMutation')(error as Error);
  };

  /* Mutation */
  const addCouponMutation = useCartMutationAddCoupon();
  const validateZipMutation = useMutation({
    mutationFn: async (zip: string) => {
      setZipValidationState(null);
      const data = await locations.getTechCoverageByZip({zip, sku_id: sku.id});

      if (data.status === 500 || data.err) {
        return {isError: true, error: data.err};
      }

      return data;
    },
    onSuccess: (data, zip) => {
      if (data.isError) {
        onInvalidZip(new Error(data.error), zip);
      } else {
        onValidZip(data, zip);
      }
    },
  });

  /* Methods */
  const resetZipValidationState = () => setZipValidationState(null);
  const openModal = () => {
    setIsModalVisible(true);
  };
  const closeModal = () => setIsModalVisible(false);
  const resetZipCodeForm = () => {
    resetZipValidationState();
    reset({
      zipCode: '',
    });
  };
  /* We are good with email */
  const allTestsPassed = ({email}: {email: string}) => {
    validateEmailWithConfig({email});
  };

  /* Submit the form */
  const onSubmitForm: SubmitHandler<YupFormProps> = async ({zipCode, email}) => {
    setIsLoading(true);
    // Trigger segment GeoGetAPrice
    SegmentTrackingAction.getAPriceClickSegment({zipCode, email, treatment: splitTreatment});
    // Validate that the zip code is in our service area
    try {
      const {
        data: {treatment},
      } = await validateZipMutation.mutateAsync(zipCode);

      const isBookingAllowed = getIsBookingAllowed(treatment);

      // add coupon to cart
      if (isBookingAllowed && !showZipOnly) {
        await addCouponMutation.mutateAsync({couponCode: 'GET10TODAY', force: true}, {onSuccess: onAddCouponSuccess, onError: onAddCouponError});
      }
    } catch (error: any) {
      logWarning(`Error in validateZipMutation for zipCode ${zipCode}: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  /* Styles */
  const inputWrapperStyles = cn(styles.inputFieldsWrapper, 'flex', {
    alignItemsEnd: showZipOnly && !errors.zipCode,
    alignItemsCenter: showZipOnly && errors.zipCode,
  });

  /* Trigger to open chat */
  const openChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    Chat?.open();
  };

  /* Each treatment as different tos. Handle here. */
  /**
   * Little helper to get the phone number from the HT_INFO_PHONE object or the default
   * @param key
   * @returns string | undefined
   */
  const getPhoneOrDefault = (key: string): string | undefined =>
    HT_INFO_PHONE.customers.zipcodeSkuGateSplit?.[key] ?? HT_INFO_PHONE.customers.default?.[key];

  const ContactDisplay: React.FC = () => (
    <p className="p1 paddingTop-small1 text-weight-medium">
      Questions?{' '}
      <span className="plainButton underline" onClick={openChat}>
        <ChatLink inline linkText="Chat with us" classesContainer={styles.chatDisplay} />
      </span>{' '}
      or call{' '}
      <a className={cn(styles.maskLinkColor, 'underline plainButton')} href={getPhoneOrDefault('link')}>
        {getPhoneOrDefault('title')}
      </a>{' '}
      to get help
    </p>
  );

  const TosDisplay: React.FC<TosDisplayProps> = props => {
    if (props.showZipOnly) {
      return null;
    }

    return (
      <p className="paddingTop-tiny1 caption n700">
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        By continuing you agree to HelloTech's{' '}
        <Link className={cn(styles.maskLinkColor, 'underline')} href={props.privacyPath()}>
          privacy policy
        </Link>
      </p>
    );
  };

  /* Effects */
  useEffect(() => {
    // zipDataCookie.zipCode will return undefined unless the client has made at least 1 attempt to verify the zip. See verifyIsBookingAllowed() above
    if (zipDataCookie.zipCode) {
      setZipValidationState(zipDataCookie.isBookingAllowed ? ZipValidationState.SUCCESS : ZipValidationState.ERROR);
    }
  }, [zipDataCookie.isBookingAllowed, zipDataCookie.zipCode]);

  /*
    If we have a logged in user, we have to assume they have been serviced.
    Set success state & set cookie
  */
  useEffect(() => {
    if (user?.id) {
      setZipValidationState(ZipValidationState.SUCCESS);
      setZipDataCookie({...zipDataCookie, zipCode: user.addressObj?.zip, isBookingAllowed: true});
    }
  }, [user]);

  if (isZipSuccessState && !sku.showZipCodeTest)
    return (
      <SkuZipCodeEntrySuccessDynamic
        className={className}
        resetZipCodeForm={resetZipCodeForm}
        onUpsellMore={onUpsellMore}
        onUpsellToggle={onUpsellToggle}
        serviceExtra={sku}
      />
    );

  return (
    <>
      <Maybe condition={!showZipOnly && !zipTreatmentIsControl}>
        <div className={styles.badge}>
          <Icon name="money" />
          <div className="text-weight-medium">Book today and get 10% off!</div>
        </div>
      </Maybe>
      <Panel mediumBorderRadius className={cn(className, styles.zipContent)}>
        <Form onSubmit={handleSubmit(allTestsPassed)}>
          <div className="paddingBottom-small">
            <p className="text-weight-medium">Check local pricing</p>
            <Maybe condition={!showZipOnly && !zipTreatmentIsControl}>
              <p className="caption n700 paddingTop-tiny">We will email you a copy of your price quote.</p>
            </Maybe>
          </div>
          <div className={inputWrapperStyles}>
            <InputField
              label="Zip Code"
              maxLength={5}
              type="text"
              inputWrapperClass={styles.input}
              containerClass={styles.zipInput}
              error={errors.zipCode?.message}
              {...register('zipCode')}
            />
            <Maybe condition={!showZipOnly && !zipTreatmentIsControl}>
              <Controller
                name="email"
                control={control}
                defaultValue=""
                render={({field}) => (
                  <EmailFieldWithValidation
                    {...field}
                    inputWrapperClass={styles.input}
                    containerClass="width-full"
                    type="text"
                    label="Email Address"
                    {...register('email')}
                    onChange={handleOnChange}
                    placeholder=""
                    error={errors.email?.message || ''}
                    softWarning={emailValidationState?.did_you_mean || ''}
                    useSuggestion={useSuggestion}
                  />
                )}
              />
            </Maybe>
            {isZipErrorState && <ErrorCalloutBox zip={zipDataCookie.zipCode} className={errorCalloutMobileTabStyles} visible={isZipErrorState} />}
            <Maybe condition={showZipOnly}>
              <Button disabled={isLoading} className={errors.zipCode ? 'marginTop-tiny1' : ''} theme={BUTTON_THEMES.PRIMARY} type="submit">
                {zipCodeCta}
              </Button>
            </Maybe>
          </div>
          {isZipErrorState && <ErrorCalloutBox zip={zipDataCookie.zipCode} className="showDesktop marginTop-small" visible={isZipErrorState} />}
          <Maybe condition={!showZipOnly && !zipTreatmentIsControl}>
            <Button disabled={isLoading} className="marginTop-small1" theme={BUTTON_THEMES.PRIMARY} type="submit">
              {zipCodeCta}
            </Button>
          </Maybe>

          <ContactDisplay />
          <TosDisplay showZipOnly={showZipOnly || zipTreatmentIsControl} privacyPath={privacyPath} />
        </Form>
        <Maybe condition={isModalVisible}>
          <SkuZipCoverageModalDynamic visible={isModalVisible} handleClose={closeModal} />
        </Maybe>
      </Panel>
    </>
  );
};

export default ZipCodeEntry;

/*
  Notes:
  testA is zip only treatment
  testB (used in ServicePage.js) is zip + email treatment
*/
